import { Constants, Effect, Matrix, PostProcess, Texture, ThinEngine, type AbstractMesh, type Camera, type Scene } from "@babylonjs/core";
import { CaptureRenderList } from "./capture-culling.ts";
import { ContactShadowReconstruction } from "./contact-shadow-reconstruction.ts";
import type { ContactShadowSamples } from "./graphics.ts";

const RECONSTRUCT = `
precision highp float;
varying vec2 vUV;
uniform highp sampler2D depthSampler;
uniform vec3 reconstructionXDepth;
uniform vec3 reconstructionXOffset;
uniform vec3 reconstructionYDepth;
uniform vec3 reconstructionYOffset;
uniform vec3 reconstructionDenominator;
uniform vec2 texel;
vec3 viewPosition(vec2 uv, float depth) {
  vec3 screen = vec3(uv * 2.0 - 1.0, 1.0);
  vec2 numerator = vec2(dot(reconstructionXDepth, screen), dot(reconstructionYDepth, screen)) * depth
    + vec2(dot(reconstructionXOffset, screen), dot(reconstructionYOffset, screen));
  return vec3(numerator / dot(reconstructionDenominator, screen), depth);
}
vec3 neighbour(vec2 uv) {
  return viewPosition(uv, texture2D(depthSampler, uv).r);
}
`;

Effect.ShadersStore.fortContactOcclusionFragmentShader = `${RECONSTRUCT}
uniform mat4 inverseView;
uniform vec2 projectionScale;
uniform float eyeHeight;
void main(void) {
  float depth = texture2D(depthSampler, vUV).r;
  if (depth <= 0.0 || depth > 500.0 || eyeHeight < 0.0) { gl_FragColor = vec4(1.0); return; }
  vec3 p = viewPosition(vUV, depth);
  float height = (inverseView * vec4(p, 1.0)).y;
  // The depth pass excludes transparent sea. Never stamp submerged rocks' AO onto it.
  float drySurface = smoothstep(0.4, 0.85, height);
  if (drySurface <= 0.0) { gl_FragColor = vec4(1.0); return; }
  vec3 left = p - neighbour(vUV - vec2(texel.x, 0.0));
  vec3 right = neighbour(vUV + vec2(texel.x, 0.0)) - p;
  vec3 down = p - neighbour(vUV - vec2(0.0, texel.y));
  vec3 up = neighbour(vUV + vec2(0.0, texel.y)) - p;
  // Choose the continuous side of a depth edge instead of deriving false silhouette normals.
  vec3 dx = abs(left.z) < abs(right.z) ? left : right;
  vec3 dy = abs(down.z) < abs(up.z) ? down : up;
  vec3 normal = normalize(cross(dx, dy));
  if (dot(normal, p) > 0.0) normal = -normal;
  float rotation = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715)))) * 6.2831853;
  float obscurance = 0.0;
  for (int i = 0; i < CONTACT_SAMPLES; i++) {
    float fi = float(i);
    // Interleave tight contact and broad crevice samples, with world-space radii.
    float radius = mod(fi, 2.0) < 0.5 ? 0.7 : 2.8;
    float r = sqrt((floor(fi * 0.5) + 0.5) / (float(CONTACT_SAMPLES) * 0.5));
    float angle = rotation + fi * 2.3999632;
    vec2 uv = vUV + vec2(cos(angle), sin(angle)) * r * radius * projectionScale / max(depth, 0.1);
    if (any(lessThan(uv, texel)) || any(greaterThan(uv, vec2(1.0) - texel))) continue;
    float sampleDepth = texture2D(depthSampler, uv).r;
    if (sampleDepth <= 0.0) continue;
    vec3 delta = viewPosition(uv, sampleDepth) - p;
    float distance = length(delta);
    float facing = max(dot(normal, delta) - 0.035, 0.0) / max(distance, 0.001);
    float range = max(1.0 - distance * distance / (radius * radius), 0.0);
    obscurance += facing * range;
  }
  float ao = exp(-4.5 * obscurance / float(CONTACT_SAMPLES));
  ao = mix(1.0, ao, drySurface * (1.0 - smoothstep(320.0, 500.0, depth)));
  gl_FragColor = vec4(vec3(ao), 1.0);
}
`;

Effect.ShadersStore.fortContactBlurFragmentShader = `
precision highp float;
varying vec2 vUV;
uniform sampler2D textureSampler;
uniform highp sampler2D depthSampler;
uniform vec2 direction;
void main(void) {
  float depth = texture2D(depthSampler, vUV).r;
  float sum = 0.0, weight = 0.0;
  for (int i = -4; i <= 4; i++) {
    float f = float(i);
    vec2 uv = vUV + direction * f;
    float sampleDepth = texture2D(depthSampler, uv).r;
    float w = exp(-f * f / 8.0 - abs(sampleDepth - depth) / max(0.04, depth * 0.002));
    sum += texture2D(textureSampler, uv).r * w;
    weight += w;
  }
  gl_FragColor = vec4(vec3(sum / max(weight, 0.0001)), 1.0);
}
`;

Effect.ShadersStore.fortContactCombineFragmentShader = `
precision highp float;
varying vec2 vUV;
uniform sampler2D textureSampler;
uniform sampler2D originalColor;
void main(void) {
  vec4 color = texture2D(originalColor, vUV);
  float ao = texture2D(textureSampler, vUV).r;
  // A slight cool bias retains sky bounce in the darkest joins instead of black soot.
  color.rgb *= mix(vec3(0.88, 0.94, 1.0), vec3(1.0), ao) * ao;
  gl_FragColor = color;
}
`;

export interface ContactShadows {
  /** Stable first pass retaining the HDR scene input, including across sample changes. */
  input: PostProcess;
  setSamples(count: ContactShadowSamples): void;
}

/** Full-resolution, world-radius contact shading before bloom and tone mapping. Camera owns the passes. */
export function attachContactShadows(scene: Scene, camera: Camera, water: AbstractMesh, sky: AbstractMesh,
  msaaSamples: number, contactSamples: ContactShadowSamples): ContactShadows | null {
  const engine = scene.getEngine();
  // Camera-space metres must not be packed/clamped into a normalized 8-bit depth target.
  if (!engine.getCaps().textureFloatRender && !engine.getCaps().textureHalfFloatRender) return null;
  const depth = scene.enableDepthRenderer(camera, false, true, Texture.NEAREST_SAMPLINGMODE, true);
  depth.useOnlyInActiveCamera = true;
  const depthMap = depth.getDepthMap();
  // DepthRenderer creates a fixed-size target; preset/viewport changes do not resize it.
  // Keep depth texels aligned with the full-resolution reconstruction and blur passes.
  const resizeDepth = () => {
    if (engine instanceof ThinEngine && engine._gl.isContextLost()) return;
    const width = engine.getRenderWidth(true), height = engine.getRenderHeight(true);
    const size = depthMap.getSize();
    if (size.width !== width || size.height !== height) depthMap.resize({ width, height });
  };
  const resized = engine.onResizeObservable.add(resizeDepth);
  const restored = engine.onContextRestoredObservable.add(resizeDepth);
  const renderList = new CaptureRenderList(scene, mesh => mesh !== water && mesh !== sky && !mesh.infiniteDistance);
  depthMap.forceLayerMaskCheck = true;
  depthMap.getCustomRenderList = () => renderList.select(camera);
  const hdrType = engine.getCaps().textureHalfFloatRender ? Constants.TEXTURETYPE_HALF_FLOAT : Constants.TEXTURETYPE_FLOAT;
  const reconstruction = new ContactShadowReconstruction(), inverseView = Matrix.Identity();
  let count = contactSamples;
  const ao = new PostProcess("contact-occlusion", "fortContactOcclusion",
    ["reconstructionXDepth", "reconstructionXOffset", "reconstructionYDepth", "reconstructionYOffset",
      "reconstructionDenominator", "inverseView", "texel", "projectionScale", "eyeHeight"], ["depthSampler"], 1,
    camera, Texture.BILINEAR_SAMPLINGMODE, engine, false, `#define CONTACT_SAMPLES ${count}`, hdrType);
  // The first pass retains the multisampled HDR scene; no extra colour-copy pass.
  ao.samples = msaaSamples;
  ao.onApply = effect => {
    const projection = camera.getProjectionMatrix();
    reconstruction.update(projection);
    camera.getViewMatrix().invertToRef(inverseView);
    effect.setVector3("reconstructionXDepth", reconstruction.xDepth);
    effect.setVector3("reconstructionXOffset", reconstruction.xOffset);
    effect.setVector3("reconstructionYDepth", reconstruction.yDepth);
    effect.setVector3("reconstructionYOffset", reconstruction.yOffset);
    effect.setVector3("reconstructionDenominator", reconstruction.denominator);
    effect.setMatrix("inverseView", inverseView);
    effect.setFloat2("projectionScale", projection.m[0]! * 0.5, projection.m[5]! * 0.5);
    effect.setFloat2("texel", 1 / ao.width, 1 / ao.height);
    effect.setFloat("eyeHeight", camera.globalPosition.y);
    effect.setTexture("depthSampler", depthMap);
  };
  for (const horizontal of [true, false]) {
    const blur = new PostProcess(horizontal ? "contact-blur-x" : "contact-blur-y", "fortContactBlur",
      ["direction"], ["depthSampler"], 1, camera, Texture.BILINEAR_SAMPLINGMODE, engine);
    blur.onApply = effect => {
      effect.setFloat2("direction", horizontal ? 1 / blur.width : 0, horizontal ? 0 : 1 / blur.height);
      effect.setTexture("depthSampler", depthMap);
    };
  }
  const combine = new PostProcess("contact-combine", "fortContactCombine", [], ["originalColor"], 1,
    camera, Texture.BILINEAR_SAMPLINGMODE, engine, false, null, hdrType);
  combine.onApply = effect => effect.setTextureFromPostProcess("originalColor", ao);
  camera.onDisposeObservable.addOnce(() => {
    engine.onResizeObservable.remove(resized);
    engine.onContextRestoredObservable.remove(restored);
    scene.disableDepthRenderer(camera);
    renderList.clear();
  });
  return {
    input: ao,
    setSamples(next) {
      if (next === count) return;
      const previous = ao.getEffect();
      ao.updateEffect(`#define CONTACT_SAMPLES ${next}`);
      // Babylon's updateEffect replaces the draw-wrapper effect without releasing
      // its createEffect reference. Balance that reference on every actual switch.
      previous?.dispose();
      count = next;
    },
  };
}
